import { addDays, isSameDay, parseISO, subDays } from 'date-fns';
import { showModal } from 'modal.react.js';
import { mergeDeepRight } from 'ramda';

import { IRoomOption } from 'components/Pages/Admin/Itinerary/Details/RoomsSelect/types';
import Preview from 'components/Pages/Admin/shared/Preview';
import { ISelectOption, TSelectChangeHandler } from 'components/shared/BaseFormComponents/Select/types';
import area from 'components/shared/GlobalModalArea/area';
import Modal from 'components/shared/GlobalModalArea/Modal';
import { EActivityEventType, EAreaEventType, EOrganizedBy, ETripEvent, ITripEvent } from 'detailsv2/structure';
import { getDefaultFormatDate } from 'utils/date';

import { TFormValues as TSuggestedAreaFormValues } from './SuggestedAreaForm/types';
export const openPagePreview = (pageId: ISelectOption['id']) => {
  return showModal(area, (res) => (
    <Modal noPadding size="lg" isOpen toClose={res} stopMouseDownPropagation>
      <Preview pageId={pageId} />
    </Modal>
  ));
};

/* 
TODO refactor The current handler is useless, the 
selector should return undefined instead of an empty object
*/
export const handleSelectPage =
  (onChange: (...event: any[]) => void): TSelectChangeHandler =>
  (name, id, config) => {
    if (id === null && name === null) return onChange(undefined);
    return onChange({ id, name, isCustom: config?.isCustom });
  };

type TSuggestedAreaFormResolver = (
  formValues: TSuggestedAreaFormValues,
  params: {
    organizedBy: EOrganizedBy;
    accommodation: Pick<ITripEvent<ETripEvent.Accommodation>, 'datesRange' | 'daysRange'>;
  },
) => (ITripEvent<ETripEvent.Area> | ITripEvent<ETripEvent.Activity>)[];
export const suggestedAreaFormResolver: TSuggestedAreaFormResolver = (formValues, { organizedBy, accommodation }) => {
  const { attachedPage, areaType } = formValues;
  if (!attachedPage) throw new Error('Invalid form data');

  const baseAreaOptions = getBaseAreaOptions(attachedPage);
  const attachedPageName = attachedPage.name;

  const startDay = accommodation.daysRange.start;
  const endDay = accommodation.daysRange.end || accommodation.daysRange.start;
  const startDate = accommodation.datesRange.start;
  const endDate = accommodation.datesRange.end || accommodation.datesRange.start;

  if (areaType === EAreaEventType.CheckInDay) {
    if (organizedBy === EOrganizedBy.Day)
      return [
        { ...baseAreaOptions, daysRange: { start: startDay, end: endDay }, datesRange: { start: null, end: null } },
      ];
    return [{ ...baseAreaOptions, datesRange: { start: startDate, end: endDate } }];
  }

  const rawActivityEvent = mergeDeepRight(baseAreaOptions, emptyMiltyDayActivity) as ITripEvent<ETripEvent.Activity>;

  if (organizedBy === EOrganizedBy.Day) {
    if (!endDay) throw new Error('Invalid form data, end day is required');

    const startActivityDay = startDay + 1;
    const endActivityDay = endDay - 1;

    const activityEvent = {
      ...rawActivityEvent,
      data: { ...rawActivityEvent.data, title: attachedPageName },
      daysRange: { start: startActivityDay, end: endActivityDay },
    };

    if (startActivityDay === endActivityDay) {
      return [{ ...activityEvent, data: { ...activityEvent.data, type: EActivityEventType.FullDay } }];
    }

    return [activityEvent];
  }

  if (!startDate || !endDate) throw new Error('Invalid form data, start and end dates are required');

  const startActivityDate = addDays(parseISO(startDate), 1);
  const endActivityDate = subDays(parseISO(endDate), 1);

  const starDateStr = getDefaultFormatDate(startActivityDate);
  const endDateStr = getDefaultFormatDate(endActivityDate);

  const activityEvent = {
    ...rawActivityEvent,
    data: { ...rawActivityEvent.data, title: attachedPageName },
    datesRange: { start: starDateStr, end: endDateStr },
  };

  if (isSameDay(startActivityDate, endActivityDate)) {
    return [{ ...activityEvent, data: { ...activityEvent.data, type: EActivityEventType.FullDay } }];
  }

  return [activityEvent];
};

const emptyMiltyDayActivity = {
  id: undefined,
  type: ETripEvent.Activity,
  positions: [],
  data: {
    title: '',
    details: null as never,
    type: EActivityEventType.MultiDay,
    notes: null,
    target: { name: '' },
    time: { start: '', end: '' },
    confirmationNumber: '' as never,
    contactInfo: '',
    website: '',
    location: '',
  },

  icon: 'fa-kit fa-defaulticon-activity',

  datesRange: { start: null, end: null, hoveredEnd: null },
  daysRange: { start: 1, end: null },
} as Partial<ITripEvent<ETripEvent.Activity>>;

const getBaseAreaOptions = (attachedPage: ISelectOption) =>
  ({
    type: ETripEvent.Area,
    icon: 'fa-kit fa-defaulticon-area',
    attachedPages: [{ id: attachedPage.id as string, name: attachedPage.name }],
    data: {
      title: `Welcome to ${attachedPage.name}`,
      details: { text: `Today you will arrive in ${attachedPage.name}` },
      notes: null,
    },
  }) as ITripEvent<ETripEvent.Area>;

export const optionToRoomAdapter = ({ label, value, isCustom, count = 0 }: IRoomOption) => ({
  id: value,
  name: label,
  count,
  ...(isCustom && { isCustom }),
});
